import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { US_MARKET } from '../constants';
import { REMOTE_HANDRAISER_CONFIG } from '../constants/environment';
import { useFetchJsonConfig } from './fetchHooks/useFetchJsonConfig';
import { isCAEntry } from '../types/hrConfigTypes';
import type { FYMTypes, OffersTypes, SuccessModalTypes } from '../types';
import useDetectDevice from './useDetectDevice';

type PreparedConfig = {
  market: string;
  dealerId: string | null;
  vehicleId: string;
  vehicleType: string;
  modelSalesCode: string | null;
  presetForm: string;
  customForm: string[];
  adobeCloudId: string | null;
  requestType: string | undefined;
  audiCode: string | null;
  carline: string | null;
  ctaInnerText: string | null;
  campaignId: string;
  isOfferPage: boolean;
  offers: OffersTypes | null;
  fym: FYMTypes | null;
  tcpaConsent: string | null | undefined;
  secondTcpaConsent: string | null | undefined;
  dealerConsent: string | null | undefined;
  dealerConsentFr: string | null | undefined;
  ccpaDisclaimer: string | null | undefined;
  ccpaConsent: string | null | undefined;
  customDepartmentName: string | null;
  customPhoneNumber: string | null;
  successModalConfig: SuccessModalTypes;
  topicOfInterests: string[];
  handraiser: string | null;
};

const useLoadAppConfig = () => {
  const deviceType = useDetectDevice();
  const { i18n } = useTranslation();

  const { hrConfig, loading } = useFetchJsonConfig(REMOTE_HANDRAISER_CONFIG);

  const getAppConfig = useCallback(
    (preparedConfig: PreparedConfig) => {
      if (!hrConfig || loading) return null;

      const { market, handraiser } = preparedConfig;

      const isUSMarket = market === US_MARKET || market?.toLowerCase().includes('us');

      const selectedConfigOptions = isUSMarket
        ? hrConfig.HANDRAISER_CONFIG_US
        : hrConfig.HANDRAISER_CONFIG_CA;

      const handraiserKey = handraiser?.toLowerCase().trim();
      const handraiserConfig =
        handraiserKey && selectedConfigOptions ? selectedConfigOptions[handraiserKey] : null;

      const isFrench = i18n.language?.toLowerCase().startsWith('fr');

      const configPayload = {
        ...preparedConfig,
        device: deviceType,
        ...(handraiserConfig && {
          campaignId: handraiserConfig.campaignId,
          formTitle: isFrench ? handraiserConfig.titleFr : handraiserConfig.title,
          derivativeCode: handraiserConfig.derivativeCode || null,
          successModalConfig:
            isFrench && isCAEntry(handraiserConfig)
              ? handraiserConfig.successModalConfigFr
              : handraiserConfig.successModalConfig || null,

          modelSelect: handraiserConfig.modelSelect || null,
        }),
      };

      return configPayload;
    },
    [deviceType, hrConfig, i18n.language, loading],
  );

  return { getAppConfig };
};

export default useLoadAppConfig;
